import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { logger } from '@immediate_media/logger';
import { getUserInfo, normaliseVanityId, type UserInfo } from '@immediate_media/auth-lib';
import useLoggedIn from '../hooks/use-logged-in';

type User = {
  email: string;
  guid: string;
  idp: string;
  role: string | null;
  vanityId: string;
};

type Context = [User | null, boolean] | undefined;

/**
 * Initialised as `undefined` to differentiate between the user being logged out
 * (when the context value will be `null`) and an attempt to access context from
 * outside a provider (when the context value will be the initial value).
 */
export const CurrentUserContext = createContext<Context>(undefined);

/**
 * Custom provider that fetches the user details and provides them in context to the children.
 */
const CurrentUserProvider = ({ children }: React.PropsWithChildren) => {
  const isLoggedIn = useLoggedIn();
  // If the user is not logged in, we initialise `loading` as false because
  // we will not try and fetch the user details.
  const [loading, setLoading] = useState(!!isLoggedIn);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    // Return early if the user is not logged in or the user details
    // have already been fetched to avoid spamming the user info endpoint.
    if (!isLoggedIn || !!currentUser) {
      return;
    }

    const fetchUserInfo = async () => {
      try {
        type UserInfoField = Exclude<keyof UserInfo, 'idToken'>;
        const fields: Array<UserInfoField> = ['email', 'idp', 'role', 'userId', 'vanityId'];
        const userInfo = await getUserInfo(fields);

        if (!userInfo) {
          setCurrentUser(null);
          return;
        }

        const user = fields.reduce<User>((acc, field) => {
          const key = field === 'userId' ? 'guid' : field;
          let value = userInfo[field];

          if (field === 'vanityId' && value) {
            value = normaliseVanityId(value);
          }

          return {
            ...acc,
            [key]: value,
          };
        }, {} as User);

        setCurrentUser(user);
      } catch (error) {
        if (error instanceof Error) {
          logger.error(`Error in current user provider useEffect: "${error.message}".`, { error });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [isLoggedIn, currentUser]);

  const value = useMemo<[User | null, boolean]>(
    () => [currentUser, loading],
    [currentUser, loading],
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export default CurrentUserProvider;
